import { FC, useState } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import styled from 'styled-components';

import { useSmartImportStore } from '@/features/smartImport/stores/smartImport.store';
import { COLORS } from '@/shared/constants/colors';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { FileMetaInfoDto } from '@/shared/lib/sj-orm/models/file-meta-info.dto';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';

import { File } from './File';

export const FileProcessing: FC = () => {
  const { t } = useTranslation('features/smartImport', { keyPrefix: 'fileProcessing' });
  const siStore = useSmartImportStore();
  const sjDb = useSJDatabase();
  const [open, setOpen] = useState(false);

  const toggleOpen = () => {
    setOpen((prev) => !prev);
  };

  const fileIds = siStore.data?.meta.file_ids as unknown as string[];
  const filesMetaInfos =
    fileIds
      ?.map((fileId) => sjDb.collections.fileMetaInfos?.findById(fileId))
      .filter((info): info is FileMetaInfoDto => Boolean(info)) ?? [];

  return (
    <Container>
      <Header>
        <HeaderContent>
          <TitleWrapper>
            <Title size={14} bold>
              {t('pending')}
            </Title>
            <Divider>•</Divider>
            <Title size={14} bold>
              {t('files', { doneCount: siStore.data?.done, totalCount: siStore.data?.total })}
            </Title>
          </TitleWrapper>
          <Description>{t('description')}</Description>
        </HeaderContent>
        <Button
          type="link"
          icon={<Icon name="arrows-out-simple" size="s24" />}
          onClick={toggleOpen}
        />
      </Header>
      <Collapse className={cn({ open })}>
        <FilesList>{filesMetaInfos?.map((file) => <File key={file.id} file={file} />)}</FilesList>
      </Collapse>
    </Container>
  );
};

const Container = styled.div`
  position: fixed;
  z-index: 10;
  bottom: 0;
  right: 32px;
  width: 400px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  background: ${COLORS.white};
  color: ${COLORS.systemDark300};
  box-shadow:
    0px 32px 32px 0px ${COLORS.systemDark200a0D},
    0px 0px 32px 0px ${COLORS.systemDark200a1A};
`;

const Header = styled.div`
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: start;
  border-bottom: 1px solid ${COLORS.systemDark100};
`;

const HeaderContent = styled.div`
  color: ${COLORS.systemDark300};
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Divider = styled.span`
  color: ${COLORS.systemDark100};
`;

const Description = styled.p`
  font-size: 11px;
`;

const Collapse = styled.div`
  height: 0;
  transition: height 0.3s ease;
  overflow-y: auto;

  &.open {
    height: 360px;
  }
`;

const FilesList = styled.div`
  padding: 12px;
`;
